import { useState } from "react";
import API from "@/lib/api";
import { CaretDown, CaretUp } from "@phosphor-icons/react";
import {
  KV,
  Section,
  PayoutGroupsTable,
  PayoutRestrictionsTable,
  ContractImagesGallery,
} from "./ContractPanelPrimitives";
import { formatSiteplugPayout } from "./payoutDisplay";

/**
 * Expandable "Program Details" panel rendered inside each expanded
 * brand row.
 *
 * Fetches the publisher-safe contract on first open — `pub_commission_pct`
 * is derived per-pub on the server (we never echo the upstream %) so the
 * panel is safe to render for any assignment.
 *
 * Renders an Impact-style "Program Terms" layout:
 *   • Header bar with payout headline (Sale: X% / passthrough display / "See details")
 *   • About / Contract Screenshots / Payout Groups / Payout Restrictions
 *   • Schedule / Qualified Referrals / Allowed Methods / General Terms
 *   • Deeplink Domains / Creative Guidelines / Restricted Traffic + Enforcement
 *   • Mandatory Initial Test (clicks/day · duration · min CR%)
 *   • Admin Notes
 *
 * Sections render lazily — if the contract has no `description`, no
 * "About" section appears (zero visual noise for sparse brands).
 */
export default function ProgramDetails({ brand }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchContract = async () => {
    if (data || loading) return;
    setLoading(true);
    try {
      const { data: res } = await API.get(`/publisher/brands/${brand.brand_id}/contract`);
      setData(res);
    } catch {
      setData({ approved: false, message: "Contact admin" });
    } finally {
      setLoading(false);
    }
  };

  const toggle = () => {
    setOpen((v) => !v);
    if (!data) fetchContract();
  };

  const list = (val) => (Array.isArray(val) ? val : []).filter(Boolean);
  const pct = data?.pub_commission_pct;
  const schedule = data?.schedule || {};
  const referral = data?.referral_policy || {};
  const general = data?.general_terms || {};

  return (
    <div className="mt-4 border-t border-slate-100 pt-3">
      <button
        onClick={toggle}
        className="flex items-center gap-1.5 text-[11px] font-bold tracking-[0.2em] uppercase text-slate-500 hover:text-[#002FA7] transition-colors"
        data-testid={`pub-program-toggle-${brand.brand_id}`}
      >
        {open ? <CaretUp size={14} /> : <CaretDown size={14} />}
        Program Details
      </button>

      {open && (
        <div className="mt-4 bg-white border border-slate-200 rounded-md">
          {loading && (
            <div className="p-6 text-xs text-slate-400">Loading…</div>
          )}

          {/* Only suppress the panel when there's truly no data on file
              (legacy brands that haven't been seeded yet). Every brand
              with a contract row — even an unapproved baseline — shows
              the standard panel below; we surface the approval state
              as a chip in the header so pubs know whether admin has
              formally signed off vs. they're seeing platform defaults. */}
          {!loading && data && !data.approved && !data.general_requirements && (
            <div className="p-6 text-xs text-slate-500 italic">
              {data.message || "Contact admin"}
            </div>
          )}

          {!loading && data && (data.approved || data.general_requirements) && (
            <>
              {/* Header bar — brand name + payout badge */}
              <div className="flex items-center justify-between gap-4 px-5 py-4 border-b border-slate-200 bg-slate-50/60">
                <div>
                  <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-slate-400 mb-0.5">
                    {brand.name}
                  </div>
                  <div className="text-[11px] text-slate-500">
                    {data.approved ? "Contract effective — live" : "Platform defaults — pending admin review"}
                  </div>
                </div>
                {data.network === "skimlinks" && data.payout_display ? (
                  <div className="flex items-baseline gap-1.5 px-3 py-1.5 rounded-md bg-[#002FA7] text-white">
                    <span className="text-[10px] font-bold tracking-[0.15em] uppercase opacity-80">Sale</span>
                    <span className="text-base font-bold">{data.payout_display}</span>
                  </div>
                ) : data.network === "siteplug" ? (
                  <div className="flex items-baseline gap-1.5 px-3 py-1.5 rounded-md bg-[#002FA7] text-white">
                    <span className="text-[10px] font-bold tracking-[0.15em] uppercase opacity-80">Sale</span>
                    <span className="text-base font-bold">
                      {formatSiteplugPayout({ ...brand, ...data })}
                    </span>
                  </div>
                ) : data.payout_mode === "passthrough" ? (
                  <div className="flex items-baseline gap-1.5 px-3 py-1.5 rounded-md bg-[#002FA7] text-white">
                    <span className="text-[10px] font-bold tracking-[0.15em] uppercase opacity-80">Sale</span>
                    <span className="text-base font-bold">
                      {data.payout_display || brand.payout_display || brand.rates_summary || "See details"}
                    </span>
                  </div>
                ) : pct > 0 ? (
                  <div className="flex items-baseline gap-1.5 px-3 py-1.5 rounded-md bg-[#002FA7] text-white">
                    <span className="text-[10px] font-bold tracking-[0.15em] uppercase opacity-80">Sale</span>
                    <span className="text-base font-bold">{pct}%</span>
                    {general.currency && (
                      <span className="text-[10px] font-mono opacity-70">{general.currency}</span>
                    )}
                  </div>
                ) : null}
              </div>

              <div className="p-5">
                {data.description && (
                  <Section title="About">
                    <p className="text-[13px] text-slate-700 leading-relaxed whitespace-pre-wrap">
                      {data.description}
                    </p>
                  </Section>
                )}

                {list(data.contract_images).length > 0 && (
                  <Section title="Contract Screenshots">
                    <ContractImagesGallery images={data.contract_images} />
                  </Section>
                )}

                {list(data.payout_groups).length > 0 && (
                  <Section title="Payout Groups">
                    <PayoutGroupsTable rows={data.payout_groups} />
                  </Section>
                )}

                {list(data.payout_restrictions).length > 0 && (
                  <Section title="Payout Restrictions">
                    <PayoutRestrictionsTable rows={data.payout_restrictions} />
                  </Section>
                )}

                {(schedule.action_locking || schedule.payout_scheduling) && (
                  <Section title="Schedule">
                    <KV label="Action Locking" value={schedule.action_locking} />
                    <KV label="Payout Scheduling" value={schedule.payout_scheduling} />
                  </Section>
                )}

                {(referral.credit_policy || referral.referral_window || data.cookie_duration) && (
                  <Section title="Qualified Referrals">
                    <KV label="Credit Policy" value={referral.credit_policy} />
                    <KV
                      label="Referral Window"
                      value={referral.referral_window || data.cookie_duration}
                    />
                    {list(data.geographic_restrictions).length > 0 && (
                      <KV
                        label="Geography"
                        value={list(data.geographic_restrictions).join(", ")}
                      />
                    )}
                  </Section>
                )}

                {list(data.allowed_methods).length > 0 && (
                  <Section title="Allowed Promotional Methods">
                    <div className="flex flex-wrap gap-1.5">
                      {list(data.allowed_methods).map((m) => (
                        <span
                          key={m}
                          className="inline-block px-2.5 py-1 rounded bg-emerald-50 border border-emerald-200 text-[12px] text-emerald-800"
                        >
                          {m}
                        </span>
                      ))}
                    </div>
                  </Section>
                )}

                {(general.currency || general.change_notification || general.reversal_policy) && (
                  <Section title="General Terms">
                    <KV label="Currency" value={general.currency} />
                    <KV label="Change Notification" value={general.change_notification} />
                    <KV label="Reversal Policy" value={general.reversal_policy} />
                  </Section>
                )}

                {list(data.deeplink_domains).length > 0 && (
                  <Section title="Deeplink Domains">
                    <div className="flex flex-wrap gap-1.5">
                      {list(data.deeplink_domains).map((m) => (
                        <span
                          key={m}
                          className="inline-block px-2 py-0.5 rounded bg-slate-100 border border-slate-200 text-[11px] text-slate-700 font-mono"
                        >
                          {m}
                        </span>
                      ))}
                    </div>
                  </Section>
                )}

                {data.creative_guidelines && (
                  <Section title="Creative Guidelines">
                    <p className="text-[13px] text-slate-700 whitespace-pre-wrap">
                      {data.creative_guidelines}
                    </p>
                  </Section>
                )}

                {(list(data.restricted_traffic_sources).length > 0 ||
                  list(data.general_requirements).length > 0 ||
                  list(data.enforcement_rights).length > 0) && (
                  <Section title="Restricted Traffic Sources (Not Allowed)">
                    {list(data.restricted_traffic_sources).length > 0 && (
                      <ol className="list-decimal ml-5 space-y-1 text-[13px] text-slate-700 mb-4">
                        {list(data.restricted_traffic_sources).map((v, i) => <li key={i}>{v}</li>)}
                      </ol>
                    )}
                    {list(data.general_requirements).length > 0 && (
                      <div className="mb-4">
                        <p className="text-[11px] font-bold tracking-[0.18em] uppercase text-slate-600 mb-2">
                          General Requirements
                        </p>
                        <ol className="list-decimal ml-5 space-y-1 text-[13px] text-slate-700">
                          {list(data.general_requirements).map((v, i) => <li key={i}>{v}</li>)}
                        </ol>
                      </div>
                    )}
                    {list(data.enforcement_rights).length > 0 && (
                      <div>
                        <p className="text-[11px] font-bold tracking-[0.18em] uppercase text-slate-600 mb-2">
                          Enforcement
                        </p>
                        <p className="text-[13px] text-slate-700 mb-2">
                          We actively monitor traffic sources and reserve the right to:
                        </p>
                        <ol className="list-decimal ml-5 space-y-1 text-[13px] text-slate-700">
                          {list(data.enforcement_rights).map((v, i) => <li key={i}>{v}</li>)}
                        </ol>
                        <p className="text-[12px] text-slate-500 italic mt-3">
                          If you have any questions about specific traffic sources or placements, please check with us before launching.
                        </p>
                      </div>
                    )}
                  </Section>
                )}

                {data.initial_test && (data.initial_test.clicks_per_day || data.initial_test.duration_days || data.initial_test.min_cr_pct) && (
                  <Section title="Mandatory Initial Test">
                    <div className="grid sm:grid-cols-3 gap-3 mb-3">
                      <div className="border border-slate-200 rounded-md p-3 bg-slate-50/40">
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Clicks / Day</div>
                        <div className="text-xl font-bold text-slate-900 font-mono">
                          {data.initial_test.clicks_per_day?.toLocaleString?.() ?? data.initial_test.clicks_per_day ?? "—"}
                        </div>
                      </div>
                      <div className="border border-slate-200 rounded-md p-3 bg-slate-50/40">
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Duration</div>
                        <div className="text-xl font-bold text-slate-900 font-mono">
                          {data.initial_test.duration_days ?? "—"}<span className="text-[12px] text-slate-500 ml-1">days</span>
                        </div>
                      </div>
                      <div className="border border-slate-200 rounded-md p-3 bg-slate-50/40">
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Min CR</div>
                        <div className="text-xl font-bold text-slate-900 font-mono">
                          {data.initial_test.min_cr_pct ?? "—"}%
                        </div>
                      </div>
                    </div>
                    {data.initial_test.notes && (
                      <p className="text-[12px] text-slate-600 italic">{data.initial_test.notes}</p>
                    )}
                  </Section>
                )}

                {data.admin_notes_html && (
                  <Section title="Notes">
                    <p className="text-[13px] text-slate-700 whitespace-pre-wrap">
                      {data.admin_notes_html}
                    </p>
                  </Section>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
